import { ProfileCardSurfaceContext } from './profileCardSurface';
import { PreviewLabelWidthContext, usePreviewLabelWidth } from './usePreviewLabelWidth';
import { Columns3, Info, MoreVertical, Pencil, Settings } from 'lucide-react';
import type { ReactNode } from 'react';
import { CountBadge } from '../CountBadge';
import { Menu, type MenuItem } from '../Menu';
import { NextActionAlert } from '../NextActionAlert';

/**
 * One card inside a `<ProfileShell>` panel — a titled group of facts about the record.
 *
 * Actions live behind a **settings dropdown** in the header, never as a bare button. A
 * section grows actions over time — edit, export, archive, regenerate — and a row of loose
 * buttons is how a header stops being readable. One cog from the start means the second
 * action costs nothing and the header never changes shape.
 *
 * `onEdit` is the conventional first entry; anything else goes in `menuItems`. The cog
 * appears only when there is something in it, so a card of read-only facts shows no control
 * at all rather than an empty menu.
 *
 * A card holding facts the system writes — created / updated / deleted, by whom and when —
 * takes neither. Audit metadata is a record of what happened and is never editable.
 */
export function ProfileCard({
  title,
  count,
  description,
  onEdit,
  editPermission,
  editDisabledReason,
  editLabel = 'Edit',
  menuItems = [],
  menuGlyph = 'settings',
  empty,
  children,
  padded = false,
  fill = false,
  id,
}: {
  title: string;
  count?: number;
  /** One line under the title, for context the fields cannot carry themselves. */
  description?: ReactNode;
  /** The conventional first menu entry. Omit for a read-only card. */
  onEdit?: () => void;
  /** Hides the entry from a caller without the key. Hide, never disable, for a pure gate. */
  editPermission?: string;
  /**
   * Why the section cannot be corrected RIGHT NOW — a state, never a permission.
   *
   * The pair `<IndexPage>` already carries as `createDisabledReason` and
   * `deleteDisabledReason`, and it is here for the same reason: a record whose own lifecycle
   * has closed — a budget plan past its submission window, a document gone obsolete — still
   * has cards a reader will reach for, and withholding the cog would say they lack a
   * permission rather than that the record has moved on. Disabled for state, with the
   * sentence in the shared `<Tooltip>`; hidden is what `editPermission` does.
   */
  editDisabledReason?: string | null;
  editLabel?: string;
  /** Further entries, rendered under the edit one. */
  menuItems?: MenuItem[];
  /**
   * The menu's trigger. The COG is the default and the convention — a card's settings, Edit
   * first. `more` (⋮, the row kebab's glyph) is for a card whose menu holds ACTIONS on what it
   * shows rather than settings of it — product call, 12 Oct 2026, for the employee survey's
   * Preview. Same `<Menu>`, same id, same gating; only the glyph and its accessible name differ.
   */
  menuGlyph?: 'settings' | 'more';
  /**
   * Set when this section holds nothing yet. The card then shows a banner inviting the reader
   * to fill it in, instead of its content.
   *
   * **A section with no data is not a section with nothing to say.** A column of em dashes, or
   * a bare sentence like "No account.", states the absence and stops — leaving the reader to
   * work out for themselves that the cog in the corner is how it gets filled. The banner says
   * the absence AND the way out of it, in the place they are already looking.
   *
   * The action is the card's own `onEdit`, carrying `editPermission`: one way to change a
   * section, offered twice in the same card rather than invented a second time. A card with no
   * `onEdit` gets the banner without a button — it states the absence, which is all a
   * read-only card can honestly do.
   *
   * `title` is the absence; `description` is optional context. Neither invents a concept the
   * record has no column for.
   */
  empty?: {
    title: string;
    description?: ReactNode;
    /** Defaults to the card's `editLabel`. */
    actionLabel?: string;
  };
  children: ReactNode;
  /**
   * Insets the content by 20px.
   *
   * **Off by default, because the usual content is a `<PreviewTable>`** — 27 of the 35 cards in
   * the product — and that table draws its own border and its own tinted label column. Inside a
   * padded body it sat as a bordered box floating in a bordered box, with a 20px moat between
   * two lines that were already saying the same thing.
   *
   * Turn it ON for content that does NOT draw its own edges: prose, a rich-text document, a
   * bare list. `<RichTextDocument>` is the case to compare against — running a paragraph
   * of body text flush into the card's border is the failure this flag exists to prevent.
   */
  padded?: boolean;
  /**
   * Stretch to the height the caller offers, the body taking whatever the header leaves.
   *
   * For content with no height of its own — a framed document, which collapses to the
   * browser's 150px default without one. **The CALLER has to offer the height**, as with
   * `<PreviewGrid fill>`: a `bleed` panel's `h-full p-4` wrapper does.
   */
  fill?: boolean;
  id?: string;
}) {
  /*
    The card owns the LABEL COLUMN's width, not the table inside it (21 Oct 2026).

    Two reasons it lives up here. The persistence key is the card's own `id` — already unique
    per profile section, so 140 `<PreviewTable>` call sites need no key of their own. And the
    `Reset width` entry belongs in a menu, which the card has and the table does not: the rail
    already established that a double-click reset is not discoverable on its own.
  */
  const labelResize = usePreviewLabelWidth(id);

  const items: MenuItem[] = [
    ...(onEdit
      ? [
          {
            key: `${id ?? 'card'}-edit`,
            label: editLabel,
            icon: <Pencil className="h-4 w-4" aria-hidden="true" />,
            permission: editPermission,
            disabledReason: editDisabledReason,
            onClick: onEdit,
          },
        ]
      : []),
    ...menuItems,
    /*
      Drawn ONLY once a width has actually been dragged. On every other card it would be a
      control that does nothing — the dead-control failure the product refuses everywhere else,
      and it would appear on cards whose content is not a field table at all.
    */
    ...(labelResize.width !== null
      ? [
          {
            key: `${id ?? 'card'}-reset-label-width`,
            label: 'Reset width',
            icon: <Columns3 className="h-4 w-4" aria-hidden="true" />,
            onClick: labelResize.reset,
          },
        ]
      : []),
  ];

  // Border is a step darker than the field table it contains: the card is the outer edge,
  // and matching tones made the two read as one flat box.
  return (
    // `overflow-hidden` so a full-bleed child is clipped to the card's radius — without it
    // the table's tinted label column paints square over the bottom corners.
    <section
      id={id}
      className={`min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white ${
        fill ? 'flex h-full min-h-0 flex-col' : ''
      }`}
    >
      {/*
        The header rule is the card's own border turned inward, so it moves with it. Left at
        slate-300 it would be a divider darker than the frame containing it, which reads as
        two boxes rather than one card with a heading.
      */}
      <header className="flex min-w-0 shrink-0 items-center gap-2 border-b border-slate-200 px-5 py-3.5">
        {/*
          `font-medium`, the product's one emphasis step — the weight the selected tab and the
          anchor column already take. It applies directly here rather than being inherited:
          Tailwind's preflight resets h1-h6 to `font-weight: inherit`, so without a class of
          its own an h3 renders at the body weight.
        */}
        <h3 className="min-w-0 truncate text-sm font-medium text-slate-800">{title}</h3>

        {count !== undefined ? <CountBadge count={count} tone="slate" /> : null}

        {items.length > 0 ? (
          <div className="ml-auto shrink-0">
            {/* `<Menu>` filters its own entries by permission, so a caller holding none of
                the keys gets an empty menu rather than a list of dead options. */}
            <Menu
              id={`${id ?? 'card'}-menu`}
              items={items}
              trigger={({ open, toggle }) => (
                <button
                  type="button"
                  id={id ? `${id}-settings` : undefined}
                  onClick={toggle}
                  aria-expanded={open}
                  aria-haspopup="menu"
                  aria-label={menuGlyph === 'more' ? `${title} actions` : `${title} settings`}
                  className={[
                    'inline-flex h-9 w-9 items-center justify-center rounded-lg border transition',
                    open
                      ? 'border-slate-400 bg-slate-100 text-slate-700'
                      : 'border-slate-300 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700',
                  ].join(' ')}
                >
                  {menuGlyph === 'more' ? (
                    <MoreVertical className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              )}
            />
          </div>
        ) : null}
      </header>

      <div className={`min-w-0 ${padded ? 'p-5' : ''} ${fill ? 'flex min-h-0 flex-1 flex-col' : ''}`}>
        {/*
          The description is prose whatever the content is, so it keeps its own inset even when
          the body has none — otherwise the one line of explanation above a full-bleed table
          would be the single thing touching the card's border.
        */}
        {description ? (
          <p className={`text-xs text-slate-500 ${padded ? 'pb-3' : 'px-5 pb-3 pt-4'}`}>
            {description}
          </p>
        ) : null}
        {/*
          The banner REPLACES the content rather than sitting above it: an empty section has
          nothing underneath worth showing, and a banner over a column of em dashes would be
          the same absence stated twice.

          `slate`, not `blue` or `amber`: nothing is wrong and nothing is in progress — the
          section is simply unfilled, which is the neutral state that tone is for. Reaching for
          amber here would put a row of attention-coloured strips down a profile of a record
          nobody has finished yet, and the reader would learn to skip them.

          It keeps its own inset whatever `padded` is, because a banner is prose rather than a
          full-bleed table and would otherwise touch the card's border.
        */}
        {empty ? (
          <div className={padded ? '' : 'px-5 pb-5 pt-4'}>
            <NextActionAlert
              id={id ? `${id}-empty` : undefined}
              tone="slate"
              icon={<Info className="h-4 w-4" aria-hidden="true" />}
              title={empty.title}
              description={empty.description}
              actionLabel={onEdit ? (empty.actionLabel ?? editLabel) : undefined}
              onAction={onEdit}
              actionPermission={editPermission}
              actionDisabledReason={editDisabledReason}
            />
          </div>
        ) : (
          <ProfileCardSurfaceContext.Provider value={!padded}>
            {/* A card with no `id` has nowhere to persist a width, so it supplies none and
                its table keeps the fixed column — see `usePreviewLabelWidth`. */}
            <PreviewLabelWidthContext.Provider value={id ? labelResize : null}>
              {children}
            </PreviewLabelWidthContext.Provider>
          </ProfileCardSurfaceContext.Provider>
        )}
      </div>
    </section>
  );
}
